/**
 * Pure price resolver (S1.5) — trace-first, shared VERBATIM by the MSW
 * handlers (mocks/handlers/pricing.ts) and the UI (W2 SO lines, W9 screens,
 * PriceTracePanel, simulator). No I/O, no globals, no Date.now — the caller
 * supplies the dataset and the as-of date, so handler and UI can never drift.
 *
 * Determination: Special Item Override → Customer Price Level → List Price,
 * stop-at-first-hit. Every tier is attempted and appears in `trace[]`; a
 * matched-but-outranked tier keeps its candidate number with skipReason
 * 'lower_precedence' (the anti-175% artifact: the trace must show the
 * override beating the level price with BOTH numbers).
 *
 * S3.1 extension (plan §S3.1) — EXTENDED, never forked:
 * - Determination order and stop-at-first-hit are PRESERVED verbatim.
 * - −Discount stages: group (Discribe-20) and quantity (DD!116/117) compose
 *   AFTER determination in the build-default order level → group → quantity
 *   (K133 = Q-L1) — each an additive entry in `discountTrace`. They run iff
 *   `dataset.productPricing` is present, so legacy datasets resolve
 *   byte-identical to S1.5.
 * - Currency mode (Discribe-19): with `input.currency` set, tier prices come
 *   ONLY from the manually-locked `currencyPriceRows` (人工鎖定) — NEVER an
 *   FX conversion; a currency without a locked row refuses with
 *   `currencySkipReason: 'no_currency_price'`.
 * - Fixed Price (Discribe-21, K126 = Q-S2) is an ENGINE guard: a winning
 *   locked row with `fixedPrice` suppresses the level discount and skips the
 *   group/quantity stages with 'fixed_price_locked'.
 * - A winning Special Item Override stays final — Price Determination Logic
 *   verbatim 「以Special Item Override 的價錢為最終售價」 — so the discount
 *   stages skip with 'special_override_final'.
 *
 * Rounding: ROUND_HALF_UP to 2dp at every derived step.
 */

import type {
  CostBreakdown,
  CostRecord,
  MarginVsLanded,
  MarkupRule,
  PriceDiscountSkipReason,
  PriceDiscountTraceEntry,
  PriceGroupBand,
  PriceResolution,
  PriceTraceEntry,
  PriceWaterfallKey,
  PriceWaterfallRow,
  PricingAnchor,
  PricingDataset,
  ResolvePriceInput,
  SpecialItemPrice,
} from '@/types/api/pricing';
import { isBandActive } from './bandSetInvariants';
import { selectCurrencyPriceRow } from './currencyMatrix';
import { applyGroupDiscount, findCustomerGroupPctDiscount } from './groups';
import {
  applyQuantityDiscount,
  getEffectiveQuantityBrackets,
  isOutsideSchedulePeriod,
  roundHalfUp2,
  selectQuantityBracket,
} from './quantityPricing';

// roundHalfUp2 moved to ./quantityPricing in S3.1 (keeps the module graph
// acyclic); re-exported here so the @/lib/pricing barrel and every existing
// consumer keep their pinned import path.
export { roundHalfUp2 } from './quantityPricing';

const fmt = (value: number): string => value.toFixed(2);

// ---------------------------------------------------------------------------
// Pure building blocks (exported for vitest + W9 screens)
// ---------------------------------------------------------------------------

/**
 * Derive the full build-up chain from stored inputs (derive-never-restore):
 * Purchase Cost + Landed Allocation = Landed Cost × (1 + 內部加價率)
 * = Pricing Base × (1 + 銷售加價率) = List Price. Each step rounded 2dp.
 */
export function buildCostBreakdown(record: CostRecord): CostBreakdown {
  const landedCost = roundHalfUp2(record.purchaseCost + record.landedAllocation);
  const pricingBase = roundHalfUp2(landedCost * (1 + record.internalMarkupPct / 100));
  const listPrice = roundHalfUp2(pricingBase * (1 + record.salesMarkupPct / 100));
  return {
    purchaseCost: record.purchaseCost,
    landedAllocation: record.landedAllocation,
    landedCost,
    internalMarkupPct: record.internalMarkupPct,
    pricingBase,
    salesMarkupPct: record.salesMarkupPct,
    listPrice,
    currency: record.currency,
  };
}

// ---------------------------------------------------------------------------
// THE PRICING ANCHOR AS A DETERMINATION INPUT (Option B, 2026-08-31)
//
// Ruled: `inputs/melissa-20260831-ruling-option-B.md` (do it) +
// RULING R-BAND / R-AD in
// `senditsell-price-band-groups-FABLE-LIVE-20260831/inputs/RULINGS-delegated-2026-08-31.md`
// (how far). Exactly ONE step moved: the sell-side MARKUP MULTIPLICATION.
// The BAND LOOKUP did not (`resolvePriceGroup` still takes the pricing base),
// and no seeded band was retired, rewritten or re-cut (R-AD).
// ---------------------------------------------------------------------------

/**
 * The engine's fallback when nothing anchors a product — neither its own pick
 * nor a company default. `pricing_base` on purpose: it reproduces the
 * pre-flip engine byte for byte, so an S1.5-shaped dataset resolves exactly
 * as it did before this slice existed.
 */
export const DEFAULT_PRICING_ANCHOR: PricingAnchor = 'pricing_base';

/**
 * THE ONE MAPPING from an anchor to the figure the markup multiplies. Both
 * duplicated ladder call sites (`attemptCustomerLevel` here and
 * `buildSourcePricing` in src/hooks/usePricing.ts) import THIS function — the
 * mapping itself cannot fork even though the ladder walk is duplicated.
 * `purchase_cost` reads `breakdown.purchaseCost`, which on the product modal's
 * trial is the LIVE Item Defined Cost (the modal rebuilds the breakdown with
 * `purchaseCost: itemDefinedCost`).
 */
export function anchorFigureOf(breakdown: CostBreakdown, anchor: PricingAnchor): number {
  switch (anchor) {
    case 'purchase_cost':
      return breakdown.purchaseCost;
    case 'landed_cost':
      return breakdown.landedCost;
    case 'pricing_base':
      return breakdown.pricingBase;
  }
}

/**
 * List Price under an anchor: the anchor's figure × (1 + 銷售加價率).
 * At `pricing_base` this is `breakdown.listPrice` exactly.
 */
export function anchorListPrice(breakdown: CostBreakdown, anchor: PricingAnchor): number {
  return roundHalfUp2(anchorFigureOf(breakdown, anchor) * (1 + breakdown.salesMarkupPct / 100));
}

/**
 * D17 precedence, resolved INSIDE the engine so every consumer of
 * `resolvePrice` gets the same answer without threading a product through six
 * call sites: the product's own pick → the company default → `pricing_base`.
 *
 * Mirrors `effectivePricingAnchor` (src/lib/pricing/anchor.ts), which answers
 * the UI's extra question ("product pick or company default?") and returns
 * null when neither is set. The two are pinned equal over every seeded product
 * by `src/__tests__/pricingAnchorDefault.test.ts` — the chip a user reads and
 * the figure they are charged cannot diverge.
 */
export function effectiveAnchorOf(
  dataset: Pick<PricingDataset, 'productAnchors' | 'pricingAnchorDefault'>,
  productId: string
): PricingAnchor {
  const own = dataset.productAnchors?.find((row) => row.productId === productId);
  if (own) return own.anchor;
  return dataset.pricingAnchorDefault ?? DEFAULT_PRICING_ANCHOR;
}

/**
 * Pick the effective cost record: matching product (and source, when given),
 * effective on/before `asOf`; 'actual' beats 'estimated'; latest effective
 * date wins within the same basis.
 */
export function selectCostRecord(
  records: CostRecord[],
  productId: string,
  asOf: string,
  source?: CostRecord['source']
): CostRecord | null {
  const candidates = records
    .filter(
      (r) =>
        r.productId === productId &&
        (source === undefined || r.source === source) &&
        r.effectiveDate <= asOf
    )
    .sort((a, b) => {
      if (a.basis !== b.basis) return a.basis === 'actual' ? -1 : 1;
      return b.effectiveDate.localeCompare(a.effectiveDate);
    });
  return candidates[0] ?? null;
}

/**
 * The product's owning PRODUCT GROUP — the input that decides WHICH BAND SET
 * buckets it. The band-set twin of `effectiveAnchorOf`, and deliberately the
 * same two-line shape: one optional derived collection, one lookup, no tree
 * walk (the depth-2 ancestor is resolved where the collection is DERIVED, so
 * the pure resolver never learns the taxonomy).
 *
 * No row ⇒ `null` ⇒ the DEFAULT band set ⇒ byte-identical to the pre-scope
 * engine. There is no company-wide default group rung on purpose: "which
 * group is this product in" has exactly one answer, and it is the product's
 * own classification. A company-level fallback would invent one.
 */
export function effectiveBandGroupOf(
  dataset: Pick<PricingDataset, 'productBandGroups'>,
  productId: string
): string | null {
  return dataset.productBandGroups?.find((row) => row.productId === productId)?.productGroupId ?? null;
}

/**
 * Select the eligible band set for a product group: the group's OWN rows when
 * it has any, otherwise the DEFAULT set (the rows carrying no scope).
 *
 * Fallback is by SET, not by row. A group that owns a band set owns all of it
 * — the default rows are not mixed back in to fill a gap the group's own cuts
 * deliberately left. Mixing would silently re-introduce a bound the operator
 * removed, which is the failure mode this whole scope exists to make visible.
 *
 * `filter` preserves array order, so when NO band carries a scope the returned
 * array is the input array, element-for-element — which is what makes the
 * absent-scope path byte-identical rather than merely equivalent.
 */
function eligibleBandSet(
  bands: PriceGroupBand[],
  productGroupId: string | null
): PriceGroupBand[] {
  const owner = resolvedBandSetOwner(bands, productGroupId);
  return owner === null
    ? bands.filter((band) => band.productGroupId === undefined)
    : bands.filter((band) => band.productGroupId === owner);
}

/**
 * WHICH SET a product group is actually banded on — its own (`productGroupId`)
 * when it owns any rows, otherwise `null` for the DEFAULT set.
 *
 * ONE BODY, because a surface that NAMES the set and an engine that SELECTS it
 * must not answer differently. P3 found exactly that drift in its first draft:
 * the currency matrix captioned "Band set: Brake Pads" for a product whose
 * group owns no cuts and which was in fact resolving on the default A–D set.
 * A label that names the wrong set is worse than no label — it is a confident
 * wrong answer about which numbers a reader is looking at.
 *
 * Ownership is by ROW EXISTENCE, not by activation (the P2 rule): a group that
 * has deactivated all its cuts still owns its set and refuses with
 * `'no_band_match'` rather than quietly reverting to the default prices.
 */
export function resolvedBandSetOwner(
  bands: readonly PriceGroupBand[],
  productGroupId: string | null
): string | null {
  if (productGroupId === null) return null;
  return bands.some((band) => band.productGroupId === productGroupId) ? productGroupId : null;
}

/**
 * Band lookup — HALF-OPEN [minInclusive, maxExclusive): a value exactly on a
 * boundary belongs to the HIGHER band (the $20 C/D overlap resolution).
 * CURRENCY-SCOPED (D27): bands are per-currency objects — only bands whose
 * `currency` equals the pricing base's own currency are candidates. No bands
 * in that currency ⇒ null ⇒ the caller's existing 'no_band_match' refusal.
 * NEVER an FX conversion, never a cross-currency fallback. `currency` is
 * REQUIRED — an optional param is a guard that fails open.
 *
 * ⚠ THE OPTION-B FLIP DID NOT TOUCH THIS FUNCTION OR ITS OPERAND (RULING
 * R-BAND, 2026-08-31). The bucketing quantity stays `breakdown.pricingBase`
 * for EVERY product, whatever its anchor, so a "band C" product is comparable
 * to every other "band C" product and the `band|source|level` markup-rule key
 * keeps indexing one axis. Moving this to the anchor's figure would re-bucket
 * products through a lookup instead of through a decision — rejected. The
 * accepted cost, stated so it is not rediscovered: a `purchase_cost`-anchored
 * product's band depends on a figure its own modal does not show, which is
 * why the band chip names the quantity it was banded on.
 *
 * ⚠ THE PRODUCT-GROUP SCOPE DOES NOT TOUCH IT EITHER. `productGroupId`
 * changes WHICH SET of bands is eligible; it does not change WHAT is banded
 * (still the pricing base) and it does not weaken D27 (the currency predicate
 * runs over the selected set exactly as before, so a group whose only bands
 * are in another currency refuses with null — it never falls back to the
 * default set's currency-matching rows). Comparability is preserved WITHIN a
 * band set, which is the axis a `band|source|level` markup rule indexes; a
 * scoped set is a different set of rules, not a re-cut of the default one
 * (RULING R-AD: A–D stays the coarse layer, nothing is retired).
 *
 * The 4th argument is OPTIONAL because absent ⇒ default set ⇒ today's
 * behaviour, which is the floor this slice is built on and the reason the
 * pinned suites run unedited. It cannot fail OPEN the way an optional
 * `currency` would: the worst a forgotten argument does is resolve on the
 * DEFAULT set — the pre-scope answer — never on a wider or foreign one. The
 * two production call sites are pinned to pass it by a source-scan guard.
 *
 * ⚠ P2 (2026-08-31) ADDS ONE PREDICATE AND NO OPERAND: `isBandActive`. Bands
 * are never hard-deleted (band CRUD deactivates), so a retired cut must stop
 * matching or the Deactivate action would be a lying artifact. `isActive` is
 * ABSENT-DEFAULT-TRUE, so every seeded row is unaffected and the pinned
 * suites again run unedited. Note the predicate lives HERE, inside the ONE
 * function both the engine and the display seat call — it is deliberately not
 * duplicated into `eligibleBandSet`, because filtering there would make a
 * fully-deactivated scoped set look UNOWNED and silently fall back to the
 * default set's prices. Ownership is by row existence; a group that has
 * deactivated all its cuts refuses with `'no_band_match'` instead.
 */
export function resolvePriceGroup(
  bands: PriceGroupBand[],
  pricingBase: number,
  currency: string,
  productGroupId: string | null = null
): PriceGroupBand | null {
  return (
    eligibleBandSet(bands, productGroupId).find(
      (band) =>
        isBandActive(band) &&
        band.currency === currency &&
        pricingBase >= band.minInclusive &&
        (band.maxExclusive === null || pricingBase < band.maxExclusive)
    ) ?? null
  );
}

/** Validity check — inclusive on both ends; `validTo: null` = open-ended. */
export function isSpecialPriceEffective(row: SpecialItemPrice, asOf: string): boolean {
  return row.validFrom <= asOf && (row.validTo === null || asOf <= row.validTo);
}

const WATERFALL_LABEL_KEYS: Record<PriceWaterfallKey, string> = {
  purchase_cost: 'pricing.waterfall.purchaseCost',
  landed_allocation: 'pricing.waterfall.landedAllocation',
  landed_cost: 'pricing.waterfall.landedCost',
  internal_markup: 'pricing.waterfall.internalMarkup',
  pricing_base: 'pricing.waterfall.pricingBase',
  sales_markup: 'pricing.waterfall.salesMarkup',
  list_price: 'pricing.waterfall.listPrice',
  level_markup: 'pricing.waterfall.levelMarkup',
  customer_discount: 'pricing.waterfall.customerDiscount',
  special_fixed: 'pricing.waterfall.specialFixed',
  special_override_pct: 'pricing.waterfall.specialOverridePct',
  // S3.1 (labels land with the S5.1 pricing.json appends):
  currency_locked_price: 'pricing.waterfall.currencyLockedPrice',
  group_discount: 'pricing.waterfall.groupDiscount',
  quantity_discount: 'pricing.waterfall.quantityDiscount',
};

function row(key: PriceWaterfallKey, formula: string | null, amount: number): PriceWaterfallRow {
  return { key, labelKey: WATERFALL_LABEL_KEYS[key], formula, amount };
}

/**
 * The build-up snapshot rows every resolution carries when costs exist.
 *
 * The cost stages (purchase → allocation → landed → base) are the RECORD's
 * own chain and never move: they are what was maintained, and the pricing-base
 * row is additionally the quantity the band was looked up on (R-BAND), so it
 * stays meaningful under every anchor. Only the `list_price` row follows the
 * anchor — and its formula NAMES its multiplicand, so the printed equation
 * holds whichever rows a reader has in front of them. At `pricing_base` the
 * output is byte-identical to the pre-flip waterfall.
 */
export function buildUpWaterfall(
  breakdown: CostBreakdown,
  anchor: PricingAnchor = DEFAULT_PRICING_ANCHOR
): PriceWaterfallRow[] {
  const anchorFigure = anchorFigureOf(breakdown, anchor);
  return [
    row('purchase_cost', null, breakdown.purchaseCost),
    row('landed_allocation', null, breakdown.landedAllocation),
    row(
      'landed_cost',
      `${fmt(breakdown.purchaseCost)} + ${fmt(breakdown.landedAllocation)}`,
      breakdown.landedCost
    ),
    row(
      'pricing_base',
      `${fmt(breakdown.landedCost)} × ${(1 + breakdown.internalMarkupPct / 100).toFixed(2)}`,
      breakdown.pricingBase
    ),
    row(
      'list_price',
      `${fmt(anchorFigure)} × ${(1 + breakdown.salesMarkupPct / 100).toFixed(2)}`,
      anchorListPrice(breakdown, anchor)
    ),
  ];
}

// ---------------------------------------------------------------------------
// The resolver
// ---------------------------------------------------------------------------

interface TierOutcome {
  entry: PriceTraceEntry;
  /** Extra waterfall rows this tier appends when it wins. */
  rows: PriceWaterfallRow[];
  /** Currency override (fixed special prices carry their own). */
  currency?: string;
  /** S3.1: the candidate came from a manually-locked per-currency row. */
  fromLockedRow?: boolean;
  /** S3.1: that locked row is fixedPrice (Discribe-21 discount immunity). */
  lockedFixedPrice?: boolean;
}

function attemptSpecialOverride(
  input: ResolvePriceInput,
  dataset: PricingDataset,
  breakdown: CostBreakdown | null
): TierOutcome {
  const tier = 'special_override' as const;
  if (input.customerId === undefined || input.customerId === null) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_customer' }, rows: [] };
  }
  const customerId = input.customerId;
  const rowsForPair = dataset.specialItemPrices.filter(
    (r) => r.customerId === customerId && r.productId === input.productId
  );
  if (rowsForPair.length === 0) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_rule_found' }, rows: [] };
  }
  const active = rowsForPair.filter((r) => r.isActive);
  if (active.length === 0) {
    return { entry: { tier, matched: false, won: false, skipReason: 'inactive' }, rows: [] };
  }
  const effective = active.filter((r) => isSpecialPriceEffective(r, input.asOf));
  if (effective.length === 0) {
    return {
      entry: { tier, matched: false, won: false, skipReason: 'outside_validity' },
      rows: [],
    };
  }
  // Deterministic pick: latest validFrom wins when several overlap.
  const winner = [...effective].sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0];
  const requestedCurrency = input.currency ?? null;

  if (winner.kind === 'fixed') {
    if (requestedCurrency !== null && winner.currency !== requestedCurrency) {
      // Discribe-19: NEVER convert — a fixed special in another currency
      // cannot serve a currency-mode request (no rule exists for it).
      return { entry: { tier, matched: false, won: false, skipReason: 'no_rule_found' }, rows: [] };
    }
    const price = roundHalfUp2(winner.fixedPrice);
    return {
      entry: { tier, matched: true, won: false, candidatePrice: price, ruleRef: winner.id },
      rows: [row('special_fixed', null, price)],
      currency: winner.currency,
    };
  }
  // markup_pct — the legacy 175% variant: the row's OWN authoring figure ×
  // (pct / 100).
  //
  // B0 recorded `basisAtAuthoring` on every row; the Option-B flip
  // (2026-08-31) is where it becomes load-bearing. The multiplicand is the
  // figure named by THE ROW, never the anchor in force today — so a migrated
  // NetSuite customer contract price authored against the pricing base keeps
  // charging exactly what it charged before the flip, and a row authored
  // after it resolves off what it was priced against. That is the whole
  // reversibility argument, and it is why this field landed one slice early.
  if (!breakdown) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_cost_record' }, rows: [] };
  }
  if (requestedCurrency !== null && breakdown.currency !== requestedCurrency) {
    // The %-override derives off the cost build-up currency — serving another
    // currency would be a conversion (banned; Discribe-19).
    return { entry: { tier, matched: false, won: false, skipReason: 'no_rule_found' }, rows: [] };
  }
  const authoringFigure = anchorFigureOf(breakdown, winner.basisAtAuthoring);
  const price = roundHalfUp2(authoringFigure * (winner.markupPct / 100));
  return {
    entry: { tier, matched: true, won: false, candidatePrice: price, ruleRef: winner.id },
    rows: [row('special_override_pct', `${fmt(authoringFigure)} × ${winner.markupPct}%`, price)],
  };
}

function attemptCustomerLevel(
  input: ResolvePriceInput,
  dataset: PricingDataset,
  breakdown: CostBreakdown | null,
  source: MarkupRule['source'] | null,
  anchor: PricingAnchor
): TierOutcome {
  const tier = 'customer_level' as const;
  if (input.customerId === undefined || input.customerId === null) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_customer' }, rows: [] };
  }
  const customerId = input.customerId;
  const pricing = dataset.customerPricing.find((c) => c.customerId === customerId);
  if (!pricing || pricing.priceLevelId === null) {
    return {
      entry: { tier, matched: false, won: false, skipReason: 'no_price_level_assigned' },
      rows: [],
    };
  }
  const level = dataset.priceLevels.find((l) => l.id === pricing.priceLevelId);
  if (!level || !level.isActive) {
    return {
      entry: { tier, matched: false, won: false, skipReason: 'price_level_inactive' },
      rows: [],
    };
  }
  const requestedCurrency = input.currency ?? null;
  if (requestedCurrency !== null) {
    // S3.1 currency mode: the level price IS the manually-locked matrix cell
    // (人工鎖定, Discribe-19) — no markup math, no FX, no fallback row.
    const lockedRow = selectCurrencyPriceRow(
      dataset.currencyPriceRows,
      input.productId,
      requestedCurrency,
      level.id
    );
    if (!lockedRow) {
      return { entry: { tier, matched: false, won: false, skipReason: 'no_rule_found' }, rows: [] };
    }
    const lockedPrice = roundHalfUp2(lockedRow.price);
    const rows: PriceWaterfallRow[] = [row('currency_locked_price', null, lockedPrice)];
    let price = lockedPrice;
    if (!lockedRow.fixedPrice && pricing.discountPct !== undefined && pricing.discountPct !== 0) {
      price = roundHalfUp2(lockedPrice * (1 - pricing.discountPct / 100));
      rows.push(
        row(
          'customer_discount',
          `${fmt(lockedPrice)} × ${(1 - pricing.discountPct / 100).toFixed(2)}`,
          price
        )
      );
    }
    return {
      entry: { tier, matched: true, won: false, candidatePrice: price, ruleRef: lockedRow.id },
      rows,
      currency: lockedRow.currency,
      fromLockedRow: true,
      lockedFixedPrice: lockedRow.fixedPrice,
    };
  }
  if (!breakdown || source === null) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_cost_record' }, rows: [] };
  }
  // PAIRED CALL SITE — keep in sync with `buildSourcePricing` in
  // src/hooks/usePricing.ts, which resolves the same band and walks the same
  // markup ladder for the product-detail display seat. The two share no
  // import edge: nothing compiled, typed or grep-able joins them, so a
  // one-sided change to either band lookup or ladder formula silently forks
  // the engine's number from the screen's. Bound by the relatedness manifest
  // entry erp-web:pricing-resolver--product-pricing-view.
  //
  // TWO OPERANDS, DELIBERATELY DIFFERENT (Option B + R-BAND, 2026-08-31):
  // the band is looked up on `breakdown.pricingBase` — unchanged, one
  // comparable axis for every product — while the markup multiplies the
  // ANCHOR's figure. Reading this as "the band is stale" is the mistake the
  // ruling anticipates: the bucketing quantity and the multiplicand are not
  // the same question.
  //
  // THIRD OPERAND, ADDED BY THE PRODUCT-GROUP SCOPE: which SET of bands is
  // eligible. Still not what is banded ON — R-BAND is untouched.
  const band = resolvePriceGroup(
    dataset.priceGroupBands,
    breakdown.pricingBase,
    breakdown.currency,
    effectiveBandGroupOf(dataset, input.productId)
  );
  if (!band) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_band_match' }, rows: [] };
  }
  const rule = dataset.markupRules.find(
    (r) => r.priceGroupId === band.id && r.source === source && r.priceLevelId === level.id
  );
  if (!rule) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_markup_rule' }, rows: [] };
  }
  const anchorFigure = anchorFigureOf(breakdown, anchor);
  const levelPrice = roundHalfUp2(anchorFigure * (1 + rule.markupPct / 100));
  const rows: PriceWaterfallRow[] = [
    row(
      'level_markup',
      `${fmt(anchorFigure)} × ${(1 + rule.markupPct / 100).toFixed(2)}`,
      levelPrice
    ),
  ];
  let price = levelPrice;
  if (pricing.discountPct !== undefined && pricing.discountPct !== 0) {
    price = roundHalfUp2(levelPrice * (1 - pricing.discountPct / 100));
    rows.push(
      row(
        'customer_discount',
        `${fmt(levelPrice)} × ${(1 - pricing.discountPct / 100).toFixed(2)}`,
        price
      )
    );
  }
  return {
    entry: { tier, matched: true, won: false, candidatePrice: price, ruleRef: rule.id },
    rows,
  };
}

function attemptListPrice(
  input: ResolvePriceInput,
  dataset: PricingDataset,
  breakdown: CostBreakdown | null,
  anchor: PricingAnchor,
  ruleRef?: string
): TierOutcome {
  const tier = 'list_price' as const;
  const requestedCurrency = input.currency ?? null;
  if (requestedCurrency !== null) {
    // S3.1 currency mode: the list price IS the locked no-level matrix row
    // (人工鎖定) — a missing row is a missing price, never a conversion.
    const lockedRow = selectCurrencyPriceRow(
      dataset.currencyPriceRows,
      input.productId,
      requestedCurrency,
      null
    );
    if (!lockedRow) {
      return { entry: { tier, matched: false, won: false, skipReason: 'no_rule_found' }, rows: [] };
    }
    const price = roundHalfUp2(lockedRow.price);
    return {
      entry: { tier, matched: true, won: false, candidatePrice: price, ruleRef: lockedRow.id },
      rows: [row('currency_locked_price', null, price)],
      currency: lockedRow.currency,
      fromLockedRow: true,
      lockedFixedPrice: lockedRow.fixedPrice,
    };
  }
  if (!breakdown) {
    return { entry: { tier, matched: false, won: false, skipReason: 'no_cost_record' }, rows: [] };
  }
  return {
    entry: {
      tier,
      matched: true,
      won: false,
      // Option B: the list tier is the anchor's figure × 銷售加價率, and the
      // build-up waterfall's closing row prints the same equation.
      candidatePrice: anchorListPrice(breakdown, anchor),
      ruleRef,
    },
    rows: [], // list_price already closes the build-up waterfall
  };
}

// ---------------------------------------------------------------------------
// S3.1 −Discount stages (level → group → quantity; K133 = Q-L1)
// ---------------------------------------------------------------------------

interface DiscountStageResult {
  entries: PriceDiscountTraceEntry[];
  rows: PriceWaterfallRow[];
  /** Price after the stages (2dp); null when determination resolved nothing. */
  price: number | null;
}

/**
 * Run the additive −Discount stages on the determined price. Every stage
 * appears exactly once in `entries` (matched or skipped-with-reason) — the
 * trace-completeness contract extended, never weakened. The level discount
 * precedes these stages INSIDE the customer_level tier (pinned S1.5 math).
 */
function runDiscountStages(
  input: ResolvePriceInput,
  dataset: PricingDataset,
  winnerOutcome: TierOutcome | null,
  determinedPrice: number | null
): DiscountStageResult {
  const entries: PriceDiscountTraceEntry[] = [];
  const rows: PriceWaterfallRow[] = [];

  if (determinedPrice === null || winnerOutcome === null) {
    entries.push({ stage: 'group_discount', matched: false, skipReason: 'no_determined_price' });
    entries.push({ stage: 'quantity_discount', matched: false, skipReason: 'no_determined_price' });
    return { entries, rows, price: determinedPrice };
  }

  const blockReason: PriceDiscountSkipReason | null =
    winnerOutcome.entry.tier === 'special_override'
      ? // 「以Special Item Override 的價錢為最終售價」(Price Determination Logic)
        'special_override_final'
      : winnerOutcome.lockedFixedPrice === true
        ? // Discribe-21 engine guard: fixedPrice beats level+group+qty.
          'fixed_price_locked'
        : null;

  if (blockReason !== null) {
    entries.push({ stage: 'group_discount', matched: false, skipReason: blockReason });
    entries.push({ stage: 'quantity_discount', matched: false, skipReason: blockReason });
    return { entries, rows, price: determinedPrice };
  }

  const fields = dataset.productPricing?.find((p) => p.productId === input.productId);
  let price = determinedPrice;
  /**
   * Did this customer hold a group discount card? Read by the quantity stage
   * when the product limits quantity discounts to card holders
   * (`quantityScope: 'card_holders'`) — a DISCOUNT-STAGE rule, never a new
   * determination tier (FABLE C2-applicability conflict B).
   */
  let holdsGroupCard = false;

  // Stage 1 of 2 — group discount (Discribe-20 大潤發 折扣卡 shape).
  if (input.customerId === undefined || input.customerId === null) {
    entries.push({ stage: 'group_discount', matched: false, skipReason: 'no_customer' });
  } else if (fields?.pricingGroupId === undefined || fields.pricingGroupId === null) {
    entries.push({ stage: 'group_discount', matched: false, skipReason: 'no_group' });
  } else {
    const card = findCustomerGroupPctDiscount(
      dataset.customerGroupDiscounts,
      input.customerId,
      fields.pricingGroupId
    );
    holdsGroupCard = card !== null;
    if (card === null) {
      // Includes 'price_level'-variant rows — uncited semantics (typed TODO
      // in groups.ts): no discount rule the engine supports.
      entries.push({ stage: 'group_discount', matched: false, skipReason: 'no_rule_found' });
    } else {
      const next = applyGroupDiscount(price, card.discountPct);
      if (card.discountPct !== 0) {
        rows.push(
          row('group_discount', `${fmt(price)} × ${(1 - card.discountPct / 100).toFixed(2)}`, next)
        );
      }
      entries.push({
        stage: 'group_discount',
        matched: true,
        candidatePrice: next,
        discountPct: card.discountPct,
        ruleRef: card.id,
      });
      price = next;
    }
  }

  // Stage 2 of 2 — quantity discount (by-line basis; blanket brackets).
  // getEffectiveQuantityBrackets THROWS on the schedule-XOR-ad-hoc violation,
  // and returns null when the referenced schedule is outside its 適用期間.
  //
  // Customer scope (2026-08-28) — the product may limit quantity discounts to
  // holders of a group discount card. Checked BEFORE the brackets so the
  // trace names the real reason.
  const brackets = getEffectiveQuantityBrackets(
    fields,
    dataset.quantityPricingSchedules,
    input.asOf
  );
  if (fields?.quantityScope === 'card_holders' && !holdsGroupCard) {
    entries.push({ stage: 'quantity_discount', matched: false, skipReason: 'customer_scope' });
  } else if (
    brackets === null &&
    isOutsideSchedulePeriod(fields, dataset.quantityPricingSchedules, input.asOf)
  ) {
    entries.push({ stage: 'quantity_discount', matched: false, skipReason: 'outside_apply_period' });
  } else if (brackets === null) {
    entries.push({ stage: 'quantity_discount', matched: false, skipReason: 'no_rule_found' });
  } else if (input.qty === undefined || !(input.qty > 0)) {
    entries.push({ stage: 'quantity_discount', matched: false, skipReason: 'no_quantity' });
  } else {
    const bracket = selectQuantityBracket(brackets, input.qty);
    if (bracket === null) {
      entries.push({ stage: 'quantity_discount', matched: false, skipReason: 'no_rule_found' });
    } else {
      const next = applyQuantityDiscount(price, bracket.discountPct);
      if (bracket.discountPct !== 0) {
        rows.push(
          row(
            'quantity_discount',
            `${fmt(price)} × ${(1 - bracket.discountPct / 100).toFixed(2)}`,
            next
          )
        );
      }
      entries.push({
        stage: 'quantity_discount',
        matched: true,
        candidatePrice: next,
        discountPct: bracket.discountPct,
        ruleRef: fields?.quantityScheduleId ?? undefined,
      });
      price = next;
    }
  }

  return { entries, rows, price };
}

/**
 * Resolve the unit price for a product (optionally for a customer) at a date.
 *
 * Returns `{unitPrice, currency, winner, trace[], waterfall[], marginVsLanded}`.
 * Winner = FIRST matched tier (stop-at-first-hit); later matched tiers keep
 * their candidate numbers with skipReason 'lower_precedence' so the trace is
 * complete (every tier appears exactly once, matched or skipped-with-reason).
 *
 * S3.1: when `dataset.productPricing` is present the −Discount stages run
 * after determination and the resolution carries `discountTrace`; when
 * `input.currency` is set, prices come only from the locked per-currency rows
 * (see the header docblock). Absent both, output is byte-identical to S1.5.
 */
export function resolvePrice(input: ResolvePriceInput, dataset: PricingDataset): PriceResolution {
  const costRecord = selectCostRecord(
    dataset.costRecords,
    input.productId,
    input.asOf,
    input.source
  );
  const breakdown = costRecord ? buildCostBreakdown(costRecord) : null;
  const source = costRecord?.source ?? input.source ?? null;
  const currencyMode = input.currency !== undefined && input.currency !== null;
  // Option B (2026-08-31): the anchor is a DETERMINATION input. Resolved once,
  // here, from the dataset — so the SO snapshot, the MSW /pricing/resolve
  // handler and the product page cannot each answer it differently.
  const anchor = effectiveAnchorOf(dataset, input.productId);

  const outcomes: TierOutcome[] = [
    attemptSpecialOverride(input, dataset, breakdown),
    attemptCustomerLevel(input, dataset, breakdown, source, anchor),
    attemptListPrice(input, dataset, breakdown, anchor, costRecord?.id),
  ];

  const winnerOutcome = outcomes.find((o) => o.entry.matched) ?? null;

  const trace: PriceTraceEntry[] = outcomes.map((o) => {
    if (o === winnerOutcome) return { ...o.entry, won: true };
    if (o.entry.matched) return { ...o.entry, skipReason: 'lower_precedence' };
    return o.entry;
  });

  const determinedPrice = winnerOutcome?.entry.candidatePrice ?? null;
  const discount =
    dataset.productPricing !== undefined
      ? runDiscountStages(input, dataset, winnerOutcome, determinedPrice)
      : null;
  const unitPrice = discount !== null ? discount.price : determinedPrice;

  // A locked-row price is a given in ITS currency — prepending the base-
  // currency build-up would draw a cross-currency waterfall (banned reading).
  const suppressBuildUp = winnerOutcome ? winnerOutcome.fromLockedRow === true : currencyMode;
  const waterfall: PriceWaterfallRow[] = [
    ...(breakdown && !suppressBuildUp ? buildUpWaterfall(breakdown, anchor) : []),
    ...(winnerOutcome ? winnerOutcome.rows : []),
    ...(discount ? discount.rows : []),
  ];

  const currency =
    winnerOutcome === null ? null : (winnerOutcome.currency ?? breakdown?.currency ?? null);

  // Margin stays comparable only within one currency: a locked row in another
  // currency than the cost build-up yields null (never a converted margin).
  const marginComparable =
    winnerOutcome === null ||
    winnerOutcome.fromLockedRow !== true ||
    (breakdown !== null && winnerOutcome.currency === breakdown.currency);
  let marginVsLanded: MarginVsLanded | null = null;
  if (unitPrice !== null && breakdown !== null && unitPrice !== 0 && marginComparable) {
    const amount = roundHalfUp2(unitPrice - breakdown.landedCost);
    marginVsLanded = { amount, pct: roundHalfUp2((amount / unitPrice) * 100) };
  }

  return {
    unitPrice,
    currency,
    pricingAnchor: anchor,
    winner: winnerOutcome ? winnerOutcome.entry.tier : 'unresolved',
    trace,
    waterfall,
    marginVsLanded,
    asOf: input.asOf,
    ...(discount !== null ? { discountTrace: discount.entries } : {}),
    ...(currencyMode && winnerOutcome === null
      ? { currencySkipReason: 'no_currency_price' as const }
      : {}),
  };
}
